import { warn } from '@easeltv/logger';
import { parseJSON } from '@easeltv/utilities';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useEventListener } from '../useEventListener/useEventListener';
import { getLocalStorage, setLocalStorage } from './useLocalStorage.helpers';

/**
 * useLocalStorage
 *
 * Hook bridges gap between localStorage and react state
 * Fetches a key from local storage, manage that state reactively and will push any updates to be override the state in
 * localStorage
 *
 * (see https://usehooks-ts.com/react-hook/use-local-storage)
 *
 * @param key - Storage key
 * @param initialValue - (Optional) Fallback value for empty state
 */
export const useLocalStorage = <T>(key: string, initialValue: T) => {
    const initialValueRef = useRef(initialValue);

    const readValue = useCallback((): T => {
        const valueRes = getLocalStorage(key);

        if (!valueRes.ok) {
            warn(`Error reading localStorage key “${key}”:`, valueRes.payload);
            return initialValueRef.current;
        }

        if (typeof valueRes.payload === 'undefined')
            return initialValueRef.current;

        const result = parseJSON<T>(valueRes.payload);

        if (!result.ok) return initialValueRef.current;

        return result.payload ?? initialValueRef.current;
    }, [key]);

    const [storedValue, setStoredValue] = useState<T>(readValue());

    const setValue = useCallback(
        (value: T | undefined) => {
            const result = setLocalStorage(key, JSON.stringify(value));

            if (result.ok) {
                window.dispatchEvent(new Event('local-storage'));
            } else {
                warn(
                    `Error setting localStorage key “${key}”:`,
                    result.payload,
                );
            }
        },
        [key],
    );

    useEffect(() => {
        setValue(readValue());
    }, [setValue, readValue]);

    const handleOnStorage = (e: StorageEvent) => {
        if (e.key === key) setStoredValue(readValue());
    };
    const handleOnLocalStorage = () => {
        setStoredValue(readValue());
    };

    useEventListener('storage', handleOnStorage);
    useEventListener('local-storage', handleOnLocalStorage);

    return [storedValue, setValue, readValue] as const;
};
